/**
 * Pure, dependency-free helpers for deriving a display domain and a
 * favicon URL from an arbitrary link. Used by the chat web-search source
 * cards (and anywhere else a link needs a favicon + domain label).
 *
 * Favicons are resolved through Google's public s2 service, which means
 * no `next.config` `remotePatterns` entry is required — consumers render
 * the result with a plain `<img>` and fall back to a globe icon on error.
 */

/**
 * Returns the hostname for `url` with a leading `www.` stripped, or
 * `null` when the input is missing or cannot be parsed into a host.
 * Accepts bare domains (no scheme) by coercing to `https://`.
 */
export function getDomainLabel(url: string | undefined | null): string | null {
  if (typeof url !== 'string') {
    return null
  }
  const trimmed = url.trim()
  if (trimmed.length === 0) {
    return null
  }

  try {
    const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`
    const host = new URL(withScheme).hostname.replace(/^www\./, '')
    return host.length > 0 ? host : null
  } catch {
    return null
  }
}

/**
 * Builds a Google s2 favicon URL for `url`, or `null` when no domain can
 * be resolved. `size` is the requested square pixel size (Google rounds
 * to the nearest supported bucket).
 */
export function getFaviconUrl(
  url: string | undefined | null,
  size = 64
): string | null {
  const domain = getDomainLabel(url)
  if (!domain) {
    return null
  }
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(
    domain
  )}&sz=${size}`
}
